import { SWRFetcher } from '@commenda/utils';
import {
  ServiceRequestStatus,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import useSWR from 'swr';
import { API_BASE_PATH } from './constants';

export interface SLASummaryCounts {
  all: number;
  onTrack: number;
  atRisk: number;
  breached: number;
}

export interface SLASummaryMeta {
  filteredTotal: number;
  counts: SLASummaryCounts;
}

export interface SLAServiceViewItem {
  id: string;
  companyName: string | null;
  corporationName: string | null;
  serviceName: string | null;
  status: ServiceRequestStatus;
  slaStatus: string | null;
  slaUtilization: number | null;
  expectedDurationOfCompletion: number | null;
  durationOfService: number | null;
  activeTasks:
    | {
        id: number;
        title: string;
        slaStatus: string | null;
        expectedTimeToComplete: number | null;
        timeElapsed: number | null;
        status: string;
        pendingOn: TaskParty | null;
      }[]
    | null;
  taskAssignees: { id: string; name: string }[] | null;
  timeOnActiveTasks: number | null;
  serviceAge: number | null;
  productId: string | null;
  serviceCompositeId?: string | null;
  serviceTitle?: string | null;
  complianceType: string | null;
  companyId: number | null;
  corporationId: number | null;
  serviceRequestId: string;
  assignedAgentFirmId: string | null;
  assignedUserId: string | null;
  createdAt: string;
  lastUpdatedAt: string;
}

export interface SLATaskViewItem {
  id: string;
  companyName: string | null;
  corporationName: string | null;
  serviceName: string | null;
  taskGroupName: string | null;
  taskName: string | null;
  taskOrder: number | null;
  taskGroupOrder: number | null;
  assignedAgentFirmName: string | null;
  assignedAgentName: string | null;
  serviceStatus: string;
  taskStatus: TaskStatus;
  slaStatus: string | null;
  slaUtilization: number | null;
  serviceCompositeId?: string | null;
  serviceTitle?: string | null;
  complianceType: string | null;
  expectedDurationOfCompletion: number | null;
  durationOfTask: number | null;
  productId: string | null;
  pendingOn: TaskParty | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  companyId: number | null;
  corporationId: number | null;
  serviceRequestId: string;
  assignedAgentFirmId: string | null;
  assignedUserId: string | null;
  taskId: number;
}

interface SLAServiceViewResponse {
  items: SLAServiceViewItem[];
  nextCursor?: string;
  meta: SLASummaryMeta;
}

interface SLATaskViewResponse {
  items: SLATaskViewItem[];
  nextCursor?: string;
  meta: SLASummaryMeta;
}

export type SLASortState = { id: string; desc: boolean } | null;

interface SLAServiceViewOptions {
  cursor?: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  slaStatus?: string;
  status?: string;
  companyId?: number;
  corporationId?: number;
  assignedAgentFirmId?: string;
  assignedUserId?: string;
  productId?: string;
  search?: string;
  complianceTypes?: string[];
  enabled?: boolean;
}

interface SLATaskViewOptions {
  cursor?: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  slaStatus?: string;
  serviceStatus?: string;
  taskStatus?: string;
  pendingOn?: string;
  companyId?: number;
  corporationId?: number;
  assignedAgentFirmId?: string;
  assignedUserId?: string;
  productId?: string;
  search?: string;
  serviceRequestId?: string;
  complianceTypes?: string[];
  enabled?: boolean;
}

function buildServiceViewQueryString(
  options: SLAServiceViewOptions
): string | null {
  if (options.enabled === false) return null;
  const q = new URLSearchParams();
  if (options.cursor) q.set('cursor', options.cursor);
  if (options.limit) q.set('limit', String(options.limit));
  if (options.sortBy) q.set('sortBy', options.sortBy);
  if (options.sortOrder) q.set('sortOrder', options.sortOrder);
  if (options.slaStatus) q.set('slaStatus', options.slaStatus);
  if (options.status) q.set('status', options.status);
  if (options.companyId) q.set('companyId', String(options.companyId));
  if (options.corporationId)
    q.set('corporationId', String(options.corporationId));
  if (options.assignedAgentFirmId)
    q.set('assignedAgentFirmId', options.assignedAgentFirmId);
  if (options.assignedUserId) q.set('assignedUserId', options.assignedUserId);
  if (options.productId) q.set('productId', options.productId);
  if (options.search) q.set('search', options.search);
  if (options.complianceTypes?.length) {
    options.complianceTypes.forEach(ct => q.append('complianceTypes', ct));
  }
  const qs = q.toString();
  return qs
    ? `${API_BASE_PATH}/sla/services?${qs}`
    : `${API_BASE_PATH}/sla/services`;
}

function buildTaskViewQueryString(options: SLATaskViewOptions): string | null {
  if (options.enabled === false) return null;
  const q = new URLSearchParams();
  if (options.cursor) q.set('cursor', options.cursor);
  if (options.limit) q.set('limit', String(options.limit));
  if (options.sortBy) q.set('sortBy', options.sortBy);
  if (options.sortOrder) q.set('sortOrder', options.sortOrder);
  if (options.slaStatus) q.set('slaStatus', options.slaStatus);
  if (options.serviceStatus) q.set('serviceStatus', options.serviceStatus);
  if (options.taskStatus) q.set('taskStatus', options.taskStatus);
  if (options.pendingOn) q.set('pendingOn', options.pendingOn);
  if (options.companyId) q.set('companyId', String(options.companyId));
  if (options.corporationId)
    q.set('corporationId', String(options.corporationId));
  if (options.assignedAgentFirmId)
    q.set('assignedAgentFirmId', options.assignedAgentFirmId);
  if (options.assignedUserId) q.set('assignedUserId', options.assignedUserId);
  if (options.productId) q.set('productId', options.productId);
  if (options.search) q.set('search', options.search);
  if (options.serviceRequestId)
    q.set('serviceRequestId', options.serviceRequestId);
  if (options.complianceTypes?.length) {
    options.complianceTypes.forEach(ct => q.append('complianceTypes', ct));
  }
  const qs = q.toString();
  return qs ? `${API_BASE_PATH}/sla/tasks?${qs}` : `${API_BASE_PATH}/sla/tasks`;
}

export function useSlaSummaryServiceView(options: SLAServiceViewOptions = {}) {
  const key = buildServiceViewQueryString(options);

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<SLAServiceViewResponse>(
      key,
      key ? SWRFetcher({ method: 'GET' }, undefined) : null
    );

  return {
    data: data?.items ?? [],
    nextCursor: data?.nextCursor,
    meta: data?.meta,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
}

export function useSlaSummaryTaskView(options: SLATaskViewOptions = {}) {
  const key = buildTaskViewQueryString(options);

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<SLATaskViewResponse>(
      key,
      key ? SWRFetcher({ method: 'GET' }, undefined) : null
    );

  return {
    data: data?.items ?? [],
    nextCursor: data?.nextCursor,
    meta: data?.meta,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
}
